import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IApiErrorResponse } from '@core/models/api-response.model';
import { ITicketNoProfilesRequest, ITicketRequest, ITicketResponse } from '@core/models/ticket.model';
import { ApiService } from '@core/services/api/api.service';
import {
  clearSsoCallerDevFlag,
  getSsoCallerOrigin,
  isSsoCallerOnLocalhost
} from '@core/utils/url.utils';
import { environment } from '@environments/environment';
import { Console } from './logger.service';

/**
 * Servizio per la gestione dei ticket SSO.
 *
 * Questo servizio si occupa di generare ticket per permettere l'accesso SSO
 * ad applicazioni esterne tramite il portale di autenticazione.
 *
 * @example
 * constructor(private ticketService: TicketService) {}
 *
 * // Genera un ticket per una target app
 * this.ticketService.generateTicket('po', 4732).subscribe({
 *   next: (response) => {
 *     // Usa url_app da getTargetApps e aggiungi il ticket come parametro
 *     const redirectUrl = `${targetAppData.url_app}?ticket=${response.ticket}`;
 *     window.location.href = redirectUrl;
 *   },
 *   error: (error) => console.error('Errore:', error)
 * });
 */
@Injectable({
  providedIn: 'root'
})
export class TicketService {
  private readonly log_label = 'TicketService';
  private readonly apiService = inject(ApiService);

  /**
   * Prepara il payload di generate-ticket.
   * Breakpoint DevTools: TicketService.prepareTicket
   */
  prepareTicket(targetApp: string, idProfilo: number): ITicketRequest {
    const request: ITicketRequest = {
      target_app: targetApp,
      id_profilo: idProfilo,
      dev: isSsoCallerOnLocalhost()
    };
    const debugPayload = {
      request,
      referrer: document.referrer || '(vuoto)',
      callerOrigin: getSsoCallerOrigin(),
      href: window.location.href
    };
    Console.log(`${this.log_label}.prepareTicket()`, debugPayload, 'info');
    if (environment.ENABLE_DEBUG_ALERTS) {
      console.info('[SSO prepareTicket]', debugPayload);
      alert(
        'auth-app : sso/generate-ticket\n' +
          JSON.stringify(debugPayload, null, 2)
      );
    }
    return request;
  }

  /**
   * Genera un ticket SSO per la target app specificata.
   *
   * @param targetApp Il codice della target app per cui generare il ticket
   * @param idProfilo L'ID del profilo utente da associare al ticket
   * @returns Observable contenente la risposta con redirect_url e ticket
   * @throws {IApiErrorResponse} Se la richiesta fallisce
   */
  generateTicket(targetApp: string, idProfilo: number): Observable<ITicketResponse> {
    const log_pref = this.log_label + '.generateTicket() ';
    const request = this.prepareTicket(targetApp, idProfilo);

    return this.apiService.post<ITicketResponse, ITicketRequest>(
      'sso/generate-ticket',
      request
    ).pipe(
      tap(() => clearSsoCallerDevFlag()),
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nella generazione del ticket.');
        Console.log(errorMessage, error, 'err');
        // Log dettagliato dell'errore per debug
        if (error.status_code === undefined) {
          Console.log(`${log_pref}Possibile errore di connessione. Verifica che l'API sia raggiungibile.`, error, 'err');
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Genera un ticket SSO per una target app quando non esistono profili.
   *
   * Usa l'endpoint dedicato Laravel `sso/generate-ticket-no-profiles`
   * che richiede solo la target_app nel payload.
   *
   * @param targetApp Il codice della target app per cui generare il ticket
   * @returns Observable contenente la risposta con redirect_url e ticket
   * @throws {IApiErrorResponse} Se la richiesta fallisce
   */
  generateTicketNoProfiles(targetApp: string): Observable<ITicketResponse> {
    const log_pref = this.log_label + '.generateTicketNoProfiles() ';
    const request: ITicketNoProfilesRequest = {
      target_app: targetApp
    };

    Console.log(`${log_pref}Generazione ticket (senza profili) per target_app: ${targetApp}`, request, 'info');

    return this.apiService.post<ITicketResponse, ITicketNoProfilesRequest>(
      'sso/generate-ticket-no-profile',
      request
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nella generazione del ticket (senza profili).');
        Console.log(errorMessage, error, 'err');
        // Log dettagliato dell'errore per debug
        if (error.status_code === undefined) {
          Console.log(`${log_pref}Possibile errore di connessione. Verifica che l'API sia raggiungibile.`, error, 'err');
        }
        return throwError(() => error);
      })
    );
  }
}
